import { Link, useLocation } from 'react-router-dom';
import { useAuth, type Role } from '../lib/auth';

/**
 * The address does not exist, said out loud.
 *
 * Until now an unknown address redirected silently to the family home. Nothing
 * broke, and that was the problem: someone following a dead link from an old
 * message, or mistyping, landed somewhere plausible and never learned that the
 * thing they were looking for was not there. A person who cannot tell the
 * difference between "this page is gone" and "you are home" cannot tell us
 * either, which means we never hear about the broken link.
 *
 * It offers the way back that fits who is signed in, rather than a list of
 * places half of which they cannot open.
 */
const HOME: Record<Role, { to: string; label: string }> = {
  parent: { to: '/', label: 'Family home' },
  grandparent: { to: '/', label: 'Family home' },
  teen: { to: '/', label: 'Family home' },
  child: { to: '/play', label: 'Play & Learn' },
  tutor: { to: '/tutor', label: 'Tutor console' },
  safeguarding_admin: { to: '/admin', label: 'Safeguarding admin' },
  content_editor: { to: '/admin', label: 'Content review' },
};

export function NotFoundPage() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const home = user ? HOME[user.role] : { to: '/signin', label: 'Sign in' };
  const isChild = user?.role === 'child';

  return (
    <section className="card stack" role="main">
      <h1>{isChild ? 'That page is not here' : 'That address does not exist'}</h1>
      <p className="lead">
        {isChild
          ? 'Nothing is broken. This page just is not one of ours.'
          : 'Nothing has gone wrong with your family’s account, and nothing has been lost. There is simply no page at this address.'}
      </p>
      {!isChild && (
        <p className="small muted">
          You asked for <code>{pathname}</code>. If you followed a link from somewhere in Ìran, please tell us — a link that leads nowhere is our mistake, not
          yours.
        </p>
      )}
      <div className="row">
        <Link className="btn" to={home.to}>
          {home.label}
        </Link>
        {!isChild && user && (
          <Link className="btn ghost small" to="/report">
            Tell us about the broken link
          </Link>
        )}
      </div>
    </section>
  );
}
